import * as _ from 'lodash';

import { getConcentrationConfigValueType } from '@finley-technologies/calc-agent';
import {
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  BorrowingBaseReportGQLType,
  BorrowingBaseReportLoanProductCalculationGQLType,
  ConcentrationLimitCalculationGQLType,
  VerificationBorrowerLoanProductCalculationGQLType,
  EligibilityCriteriaCalculationGQLType,
  VerificationBorrowerParsedBorrowingBaseReportGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';

export const BorrowingBaseSummarySectionsIterable = [
  'facilitySummary',
  'receivables',
  'cash',
  'finalBorrowingBaseReport',
] as const;
export type BorrowingBaseSummarySectionsType = typeof BorrowingBaseSummarySectionsIterable[number];

export const BorrowingBaseSummarySections = {
  facilitySummary: 'facilitySummary',
  receivables: 'receivables',
  cash: 'cash',
  finalBorrowingBaseReport: 'finalBorrowingBaseReport',
} as const;
// eslint-disable-next-line no-redeclare
export type BorrowingBaseSummarySections = BorrowingBaseSummarySectionsType;

export type BorrowingBaseSummaryValueType =
  | 'currency'
  | 'percentage'
  | 'decimal'
  | 'integer'
  | 'string';

export type BorrowingBaseSummaryInput = {
  minuend: BorrowingBaseReportGQLType;
  subtrahend?: VerificationBorrowerParsedBorrowingBaseReportGQLType;
  reference?: BorrowingBaseReportGQLType;
};

export type BorrowingBaseSummaryColumnValueInput = {
  minuendValue?: Util.Maybe<number | string>;
  subtrahendValue?: Util.Maybe<number | string>;
  referenceValue?: Util.Maybe<number | string>;
};

export type LoanProductCalculationsBorrowingBaseSummaryInput = {
  minuendBorrowingBaseReport?: Util.Maybe<LoanProductCalculationsForBorrowingBaseSummary>;

  subtrahendBorrowingBaseReport?: Util.Maybe<LoanProductCalculationsForBorrowingBaseSummary>;
  referenceBorrowingBaseReport?: Util.Maybe<LoanProductCalculationsForBorrowingBaseSummary>;
};

type BorrowingBaseSummaryAccordionRowFields = Pick<
  BorrowingBaseReportGQLType,
  | 'commitmentAmount'
  | 'currentPrincipalBalance'
  | 'totalReceivableExposure'
  | 'totalEligibilityKickout'
  | 'totalEligibleExposure'
  | 'totalConcentrationKickout'
  | 'borrowingBaseAmountAssets' // to be renamed 'totalEligibleExposureLessConcentrationKickouts'
  | 'weightedAverageReceivableAdvanceRate'
  | 'maxReceivableAdvanceAmount' // to be renamed totalReceivableBorrowingBaseAmount
  | 'effectiveReceivableAdvanceRate'
  | 'totalPreAdvanceCashContribution'
  | 'effectiveCashAdvanceRate'
  | 'totalCashBorrowingBaseAmount'
  | 'totalBorrowingBaseAmount' // will migrate calculation definition
  | 'totalAssets'
  | 'effectiveAdvanceRate'
  | 'borrowingBaseReserveAmount'
  | 'totalCollectionsAccountCash'
  | 'maximumAvailableAmount'
  | 'borrowingBaseAvailability'
  | 'borrowingBaseAvailabilityRate'
>;

export type BorrowingBaseSummaryLoanProductCalculationsAccordionRowFields = Pick<
  BorrowingBaseReportLoanProductCalculationGQLType,
  | 'totalReceivableExposure'
  | 'totalEligibilityKickout'
  | 'totalEligibleExposure'
  | 'totalConcentrationKickout'
  | 'totalEligibleExposureLessConcentrationKickouts'
  | 'weightedAverageReceivableAdvanceRate'
  | 'totalReceivableBorrowingBaseAmount'
  | 'effectiveReceivableAdvanceRate'
>;

export type AllBorrowingBaseReportSummaryFields =
  | keyof BorrowingBaseReportGQLType
  | keyof VerificationBorrowerParsedBorrowingBaseReportGQLType
  | keyof EligibilityCriteriaCalculationGQLType
  | keyof ConcentrationLimitCalculationGQLType
  | keyof BorrowingBaseReportLoanProductCalculationGQLType
  | 'concentrationLimitAmount'
  | 'cashBalanceForAccount';

export type LinkAddressAndLabel = {
  linkAddress: string;
  linkLabel: string;
};

type LoanProductCalculationsForBorrowingBaseSummary = (
  | BorrowingBaseReportLoanProductCalculationGQLType
  | VerificationBorrowerLoanProductCalculationGQLType
)[];

type VerificationValueComparison =
  | 'borrowerHigherIsMorePunitive'
  | 'borrowerHigherIsLessPunitive';

export const verificationRawValueRoundingErrorAllowance = 0.01;

// if true: borrower value > finley value = good
// if false: borrower value > finley value = bad
const isBorrowerHigherValueMorePunitiveMap: Partial<
  Record<AllBorrowingBaseReportSummaryFields, VerificationValueComparison>
> = {
  // borrowing base report fields
  currentPrincipalBalance: 'borrowerHigherIsLessPunitive',
  commitmentAmount: 'borrowerHigherIsLessPunitive',
  totalReceivableExposure: 'borrowerHigherIsLessPunitive',
  totalEligibilityKickout: 'borrowerHigherIsMorePunitive',
  totalEligibleExposure: 'borrowerHigherIsLessPunitive',
  totalConcentrationKickout: 'borrowerHigherIsMorePunitive',
  borrowingBaseAmountAssets: 'borrowerHigherIsLessPunitive', // @danitian borrowing-base migration: will be renamed to totalEligibleExposureLessConcentrationKickouts
  weightedAverageReceivableAdvanceRate: 'borrowerHigherIsLessPunitive',
  maxReceivableAdvanceAmount: 'borrowerHigherIsLessPunitive', // @danitian borrowing-base migration: will be renamed to totalReceivableBorrowingBaseAmount
  effectiveReceivableAdvanceRate: 'borrowerHigherIsLessPunitive',
  totalPreAdvanceCashContribution: 'borrowerHigherIsLessPunitive',
  effectiveCashAdvanceRate: 'borrowerHigherIsLessPunitive',
  totalCashBorrowingBaseAmount: 'borrowerHigherIsLessPunitive',
  totalBorrowingBaseAmount: 'borrowerHigherIsLessPunitive',
  totalAssets: 'borrowerHigherIsLessPunitive',
  effectiveAdvanceRate: 'borrowerHigherIsLessPunitive',
  cashBalanceForAccount: 'borrowerHigherIsLessPunitive',

  // eligibility calculations fields
  eligibilityReduction: 'borrowerHigherIsMorePunitive',

  // concentration calculations fields
  concentrationAmount: 'borrowerHigherIsMorePunitive',
  totalEligibleExposureLessConcentrationKickouts:
    'borrowerHigherIsLessPunitive',
  concentration: 'borrowerHigherIsMorePunitive',
  concentrationLimitAmount: 'borrowerHigherIsLessPunitive',
};

// distinguish BorrowingBaseReportGQLType from VerificationBorrowerParsedBorrowingBaseReport
const isVerificationBorrowerParsedAndNotBorrowingBaseReport = (
  test: any,
): test is VerificationBorrowerParsedBorrowingBaseReportGQLType => {
  // a regular borrowing base report always carries an id whereas verification parsed borrowing base report does not
  return test && !test.id;
};

const defaultDisplayString = '-';
export const defaultBorrowingBaseSummaryColumnValueInput: BorrowingBaseSummaryColumnValueInput = {
  minuendValue: null,
  subtrahendValue: null,
  referenceValue: null,
};

// @limit_formula_todo: revisit this when all limits are formula.
export const borrowingBaseSummaryFormatterMap: {
  [key in BorrowingBaseSummaryValueType]: (
    value: Util.Maybe<number | string>,
  ) => string;
} = {
  currency: (value) =>
    _.isNumber(value)
      ? commonUtils.numberToCurrencyWithSymbol({
          number: value,
        }) ?? defaultDisplayString
      : defaultDisplayString,
  percentage: (value) =>
    _.isNumber(value)
      ? commonUtils.numberToFormattedString(value, 'percentage', false)
      : defaultDisplayString,
  decimal: (value) =>
    _.isNumber(value) ? value.toFixed(2) : defaultDisplayString,
  integer: (value) =>
    _.isNumber(value) ? value.toFixed(0) : defaultDisplayString,
  string: (value) => (value ? value.toString() : defaultDisplayString),
};

export const getNumberedSubRowLabel = (
  index: number,
  label: string,
): string => {
  const nbsps = `${'\u00A0'}${'\u00A0'}${'\u00A0'}`;
  return `${index + 1}${nbsps}${commonUtils.toTitleCase(label)}`;
};

export const getBankAccountCashAdvanceRateSubRowLabel = (
  accountType: Bank.AccountType,
): string => {
  return `${commonUtils.toTitleCase(accountType)} Account Rate`;
};

export const getContributingBankAccountAdvanceRates = ({
  borrowers,
}: {
  borrowers: CreditAgreement.DomainEntity.Items.BorrowerEntity[];
}): {
  accountType: Bank.AccountType;
  cashAdvanceRate: string | undefined;
  bankAccountId: string;
}[] => {
  const bankAccounts = _.chain(borrowers)
    .flatMap(({ bankAccounts: borrowerBankAccounts }) => borrowerBankAccounts)
    .map((bankAccount) => {
      if (
        commonTypeguards.isBorrowingBaseContributionAccountType(
          bankAccount.type.value,
        )
      ) {
        return {
          accountType: bankAccount.type.value,
          cashAdvanceRate: bankAccount.cashAdvanceRate?.value,
          bankAccountId: bankAccount.entity.id,
        };
      }
      return null;
    })
    .compact()
    .value();

  return bankAccounts;
};

export const isPointerMethodCalculationConfig = (
  calculationConfig: any,
): calculationConfig is CreditAgreement.Config.ConcentrationLimit.Calculation.PointerMethodCalculationConfig => {
  return calculationConfig?.type === 'pointer';
};
export const isDeclarativeFilterConcentrationLimitCalculationConfig = (
  calculationConfig: any,
): calculationConfig is CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterCalculationMethodConfig => {
  return CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeConfigIterable.includes(
    calculationConfig?.type,
  );
};
export const isWeightedAverageConcentrationLimitCalculationConfig = (
  calculationConfig: any,
): calculationConfig is CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterWeightedAverageCalculationConfig => {
  return calculationConfig?.type === 'weightedAverage';
};
export const getDoesConcentrationLimitHaveFilters = (
  concentrationLimitConfig: any,
): boolean => {
  const calculationConfig =
    concentrationLimitConfig._config_?.concentrationLimitConfig
      ?.calculationConfig;

  return (
    isDeclarativeFilterConcentrationLimitCalculationConfig(calculationConfig) &&
    calculationConfig?.config.filters.length > 0
  );
};
export const getFormattedStringsForDeclarativeFilterOperations = (
  calculationConfig: CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterCalculationMethodConfig,
): string[] => {
  const filterConfigs = calculationConfig.config.filters;
  return _.map(filterConfigs, (filterConfig) => {
    const { field, operation } = filterConfig;
    const splitField = field.split('.');
    const fieldName = splitField[splitField.length - 1];

    switch (operation) {
      case 'greaterThan':
        return `${fieldName} > ${filterConfig.value}`;
      case 'greaterThanOrEqual':
        return `${fieldName} >= ${filterConfig.value}`;
      case 'lessThan':
        return `${fieldName} < ${filterConfig.value}`;
      case 'lessThanOrEqual':
        return `${fieldName} <= ${filterConfig.value}`;
      case 'equals':
        return `${fieldName} = ${filterConfig.value}`;
      case 'notEqual':
        return `${fieldName} != ${filterConfig.value}`;
      case 'orderBySum': {
        const splitGroup = filterConfig.groupBy.split('.');
        // example: 'Largest 1 by state'
        return `Largest ${filterConfig.count} by ${
          splitGroup[splitGroup.length - 1]
        }`;
      }
      case 'orderBy':
        return `Largest ${filterConfig.count} ${fieldName}`;
      case 'inList':
        return `${fieldName} in: ${filterConfig.list.join(', ')}`;
      case 'notInList':
        return `${fieldName} not in: ${filterConfig.list.join(', ')}`;
      default:
        return fieldName;
    }
  });
};

export const getFormattedStringsForWeightedAverageFields = (
  calculationConfig: CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterWeightedAverageCalculationConfig,
): string[] => {
  const { field, weighting } = calculationConfig.config;
  const splitField = field.split('.');
  const fieldName = splitField[splitField.length - 1];
  return [
    `Field: ${fieldName}`,
    `Weight: ${commonUtils.toTitleCase(weighting)}`,
  ];
};

export const getConcentrationLimitRowTemplateColumns = ({
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
}: {
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
}) => {
  if (showComparisonAndDifferenceColumns) {
    return '5fr repeat(4, 3fr) 1fr';
  }
  if (showValueAndReferenceColumnsOnly) {
    return `repeat(4, 1fr)`;
  }

  return 'repeat(3, 1fr)';
};

export const getConcentrationLimitConfigCartsTemplateColumns = ({
  isWeightedAverage,
  hasFilters,
}: {
  isWeightedAverage: boolean;
  hasFilters: boolean;
}): string => {
  const twoColumns = 'repeat(2, 1fr)';
  const threeColumns = 'repeat(3, 1fr)';

  if (isWeightedAverage && hasFilters) {
    return threeColumns;
  }

  return twoColumns;
};

export const getFormattedConcentrationLimit = (
  concentrationLimitConfig: CreditAgreement.DomainEntity.ConcentrationLimitProperty,
  historicalConcentrationLimit?: number,
): string | undefined => {
  const valueConfig =
    concentrationLimitConfig._config_?.concentrationLimitConfig?.valueConfig;
  const calculationConfig =
    concentrationLimitConfig._config_?.concentrationLimitConfig
      ?.calculationConfig;
  const limitType =
    calculationConfig?.type === 'weightedAverage'
      ? calculationConfig.limitType
      : 'max';

  const config = concentrationLimitConfig?._config_.concentrationLimitConfig;
  const concentrationLimitValueType = getConcentrationConfigValueType(config);

  if (!valueConfig) {
    return undefined;
  }

  const concentrationLimitCurrentCreditAgreementValue = Number.isNaN(
    Number(valueConfig.limit),
  )
    ? valueConfig.limit
    : Number(valueConfig.limit);

  // if there is a historical concentration limit value, use that instead of the most current credit agreement limit value
  const concentrationLimitValue =
    historicalConcentrationLimit ||
    concentrationLimitCurrentCreditAgreementValue;

  const formattedConcentrationLimitValue = borrowingBaseSummaryFormatterMap[
    concentrationLimitValueType === 'number'
      ? 'decimal'
      : concentrationLimitValueType
  ](concentrationLimitValue);

  return valueConfig
    ? `${_.startCase(limitType)} ${formattedConcentrationLimitValue}`
    : undefined;
};

export type ExpandedConcentrationLimitCalculations = Pick<
  ConcentrationLimitCalculationGQLType,
  | 'borrowingBaseReduction'
  | 'concentration'
  | 'concentrationAmount'
  | 'concentrationLimit'
  | 'totalEligibleExposure'
> & {
  totalEligibleExposureLessConcentrationKickouts: number;
  concentrationLimitAmount: Util.Maybe<number>;
};

const formatExpandedBorrowingBaseReportConcentration = ({
  borrowingBaseReport,
  concentrationLimitKey,
  concentrationLimitConfig,
}: {
  borrowingBaseReport?:
    | BorrowingBaseReportGQLType
    | VerificationBorrowerParsedBorrowingBaseReportGQLType;
  concentrationLimitKey: string;
  concentrationLimitConfig?: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
}): ExpandedConcentrationLimitCalculations | undefined => {
  if (!borrowingBaseReport) {
    return undefined;
  }
  const concentrationLimitCalculations = _.find(
    borrowingBaseReport.concentrationLimitCalculations,
    (concentrationLimitCalc) =>
      concentrationLimitCalc.concentrationLimitKey === concentrationLimitKey,
  );
  if (!concentrationLimitCalculations) {
    return undefined;
  }

  // only show 'concentrationAmount' and 'concentrationLimitAmount' if the config type is 'standard'
  const showConcentrationAmountAndConcentrationLimitAmount =
    concentrationLimitConfig?._config_?.concentrationLimitConfig
      ?.calculationConfig?.type === 'standard';

  const eligibleExposure = concentrationLimitCalculations.totalEligibleExposure;

  return {
    concentrationLimit: concentrationLimitCalculations.concentrationLimit,
    concentration: concentrationLimitCalculations.concentration,
    concentrationAmount: showConcentrationAmountAndConcentrationLimitAmount
      ? concentrationLimitCalculations.concentrationAmount
      : null,
    borrowingBaseReduction:
      concentrationLimitCalculations.borrowingBaseReduction,
    totalEligibleExposure: eligibleExposure,
    // The behavior here matches the legacy behavior, but it is incorrect because it does not
    // account for waterfall kickouts. It will be adjusted in getExpandedConcentrationCalcs.
    totalEligibleExposureLessConcentrationKickouts:
      eligibleExposure - concentrationLimitCalculations.borrowingBaseReduction,
    concentrationLimitAmount: showConcentrationAmountAndConcentrationLimitAmount
      ? concentrationLimitCalculations.concentrationLimit *
        concentrationLimitCalculations.totalEligibleExposure
      : null,
  };
};

export const getExpandedConcentrationCalcs = ({
  borrowingBaseReports,
  concentrationLimitCalcs,
  concentrationLimitConfigMap,
  useWaterfallExposure,
}: {
  borrowingBaseReports: BorrowingBaseSummaryInput;
  concentrationLimitCalcs: {
    minuend: ConcentrationLimitCalculationGQLType[];
    subtrahend?: ConcentrationLimitCalculationGQLType[];
    reference?: ConcentrationLimitCalculationGQLType[];
  };
  concentrationLimitConfigMap: Map<
    string,
    CreditAgreement.DomainEntity.ConcentrationLimitProperty
  >;
  useWaterfallExposure: boolean;
}): {
  concentrationLimitKey: string;
  expandedCalcs: {
    minuend?: ExpandedConcentrationLimitCalculations;
    subtrahend?: ExpandedConcentrationLimitCalculations;
    reference?: ExpandedConcentrationLimitCalculations;
  };
}[] => {
  let minuendWaterfallExposure =
    borrowingBaseReports.minuend.totalEligibleExposure ?? null;
  let referenceWaterfallExposure =
    borrowingBaseReports.reference?.totalEligibleExposure ?? null;
  return concentrationLimitCalcs.minuend.map((minuendCalc) => {
    const { concentrationLimitKey } = minuendCalc;
    const concentrationLimitConfig = concentrationLimitConfigMap.get(
      concentrationLimitKey,
    );

    const referenceCalc = concentrationLimitCalcs.reference?.find(
      (c) => c.concentrationLimitKey === concentrationLimitKey,
    );

    if (minuendWaterfallExposure !== null) {
      minuendWaterfallExposure -= minuendCalc.borrowingBaseReduction;
    }
    if (referenceWaterfallExposure !== null && referenceCalc) {
      referenceWaterfallExposure -= referenceCalc.borrowingBaseReduction;
    }

    const minuendExpandedCalcs = formatExpandedBorrowingBaseReportConcentration(
      {
        borrowingBaseReport: borrowingBaseReports.minuend,
        concentrationLimitKey,
        concentrationLimitConfig,
      },
    );
    const subtrahendExpandedCalcs = formatExpandedBorrowingBaseReportConcentration(
      {
        borrowingBaseReport: borrowingBaseReports.subtrahend,
        concentrationLimitKey,
        concentrationLimitConfig,
      },
    );
    const referenceExpandedCalcs = formatExpandedBorrowingBaseReportConcentration(
      {
        borrowingBaseReport: borrowingBaseReports.reference,
        concentrationLimitKey,
        concentrationLimitConfig,
      },
    );

    // The minuend (current borrowing base report) and reference (previous borrowing base report)
    // are computed by Finley. `totalEligibleExposureLessConcentrationKickouts` here does not
    // properly account for waterfall kickouts, so they are adjusted here.
    //
    // TODO(jon): understand what the expected subtrahend (borrower numbers) should be. We may need
    //  to adjust these as well.
    if (useWaterfallExposure) {
      if (minuendExpandedCalcs && minuendWaterfallExposure !== null) {
        minuendExpandedCalcs.totalEligibleExposureLessConcentrationKickouts = minuendWaterfallExposure;
      }
      if (referenceExpandedCalcs && referenceWaterfallExposure !== null) {
        referenceExpandedCalcs.totalEligibleExposureLessConcentrationKickouts = referenceWaterfallExposure;
      }
    }

    return {
      concentrationLimitKey,
      expandedCalcs: {
        minuend: minuendExpandedCalcs,
        subtrahend: subtrahendExpandedCalcs,
        reference: referenceExpandedCalcs,
      },
    };
  });
};

export const getConcentrationLimitConfigMapFromCreditAgreement = (
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): Map<string, CreditAgreement.DomainEntity.ConcentrationLimitProperty> => {
  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: {
        loanProductMechanics,
        multiLoanProductConcentrationLimits,
      },
    },
  } = creditAgreementFullArtifact;

  const singleLoanProductConcentrationLimits = _.flatMap(
    loanProductMechanics,
    (loanProduct) => loanProduct.concentrationLimits,
  );
  const allConcentrationLimits = _.concat(
    singleLoanProductConcentrationLimits,
    multiLoanProductConcentrationLimits,
  );

  const concentrationLimitConfigsMap = new Map<
    string,
    CreditAgreement.DomainEntity.ConcentrationLimitProperty
  >();

  _.forEach(allConcentrationLimits, (concentrationLimit) => {
    const config = concentrationLimit._config_?.concentrationLimitConfig;
    if (config) {
      concentrationLimitConfigsMap.set(config.propertyKey, concentrationLimit);
    }
  });

  return concentrationLimitConfigsMap;
};

export const getTemplateColumnsForAccordionRow = ({
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
}: {
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
}) => {
  if (showComparisonAndDifferenceColumns) {
    return '5fr repeat(4, 3fr) 1fr';
  }
  if (showValueAndReferenceColumnsOnly) {
    return 'repeat(4, 1fr)';
  }

  return 'repeat(5, 1fr)';
};

export const getColumnValuesForBorrowingBaseReportField = ({
  borrowingBaseReports,
  fieldName,
}: {
  borrowingBaseReports: BorrowingBaseSummaryInput;
  fieldName: keyof BorrowingBaseSummaryAccordionRowFields;
}): BorrowingBaseSummaryColumnValueInput => {
  return {
    minuendValue: borrowingBaseReports.minuend[fieldName] ?? undefined,
    subtrahendValue: borrowingBaseReports.subtrahend?.[fieldName],
    referenceValue: borrowingBaseReports.reference?.[fieldName],
  };
};

export const getColumnValuesForLoanProductCalculationsField = ({
  loanProductCalculationsForBorrowingBaseReports,
  loanProductId,
  fieldName,
}: {
  loanProductCalculationsForBorrowingBaseReports: LoanProductCalculationsBorrowingBaseSummaryInput;
  loanProductId: string;
  fieldName: keyof BorrowingBaseSummaryLoanProductCalculationsAccordionRowFields;
}): BorrowingBaseSummaryColumnValueInput => {
  const {
    minuendBorrowingBaseReport,
    subtrahendBorrowingBaseReport,
    referenceBorrowingBaseReport,
  } = loanProductCalculationsForBorrowingBaseReports;

  const minuendLpCalcs = _.find(minuendBorrowingBaseReport, (lpCalc) => {
    return lpCalc.loanProductId === loanProductId;
  });

  const subtrahendLpCalcs = _.find(subtrahendBorrowingBaseReport, (lpCalc) => {
    return lpCalc.loanProductId === loanProductId;
  });

  const referenceLpCalcs = _.find(referenceBorrowingBaseReport, (lpCalc) => {
    return lpCalc.loanProductId === loanProductId;
  });

  return {
    minuendValue: minuendLpCalcs?.[fieldName],
    subtrahendValue: subtrahendLpCalcs?.[fieldName],
    referenceValue: referenceLpCalcs?.[fieldName],
  };
};

/**
 * This util is only in place until totalBorrowingBaseAmount and effectiveAdvanceRate calcs have been migrated
 */
export const calculateTotalBorrowingBaseAmountAndEffectiveAdvanceRate = ({
  borrowingBaseReport,
}: {
  borrowingBaseReport?:
    | BorrowingBaseReportGQLType
    | VerificationBorrowerParsedBorrowingBaseReportGQLType;
}): {
  totalBorrowingBaseAmount: number | null;
  effectiveAdvanceRate: number | null;
} | null => {
  if (!borrowingBaseReport) {
    return null;
  }

  const {
    maxReceivableAdvanceAmount,
    totalAssets,
    totalCashBorrowingBaseAmount,
    totalBorrowingBaseAmount,
  } = borrowingBaseReport;

  const totalReceivableBorrowingBaseAmount = maxReceivableAdvanceAmount;

  if (
    isVerificationBorrowerParsedAndNotBorrowingBaseReport(borrowingBaseReport)
  ) {
    // if we are looking at a verification borrowing base report
    // and we will use the parsed totalBorrowingBaseAmount instead of deriving it
    // @dani will no longer need to keep this in a conditional after phase 3 borrowing base migration
    if (_.isNumber(totalBorrowingBaseAmount)) {
      return {
        totalBorrowingBaseAmount,
        effectiveAdvanceRate: _.isNumber(totalAssets)
          ? commonUtils.divideByOrZero(totalBorrowingBaseAmount, totalAssets)
          : null,
      };
    }
    return null;
  }
  // @dani will no longer need to derive totalBorrowingBaseAmount after phase 3 borrowing base migration
  const totalBorrowingBaseAmountDerived =
    (totalReceivableBorrowingBaseAmount ?? 0) +
    (totalCashBorrowingBaseAmount ?? 0);

  const effectiveAdvanceRateDerived = _.isNumber(totalAssets)
    ? commonUtils.divideByOrZero(totalBorrowingBaseAmountDerived, totalAssets)
    : null;

  return {
    totalBorrowingBaseAmount: totalBorrowingBaseAmountDerived,
    effectiveAdvanceRate: effectiveAdvanceRateDerived,
  };
};

/**
 * The logic that determines whether there is a difference between values
 * should use the same precision as we use when printing values for display.
 * @param minuend the starting value
 * @param subtrahend the value to subtract
 */
const getBorrowingBaseValueDifference = ({
  minuend,
  subtrahend,
  valueType,
}: {
  minuend?: Util.Maybe<any>;
  subtrahend?: Util.Maybe<any>;
  valueType: BorrowingBaseSummaryValueType;
}): number | undefined => {
  if (!_.isNumber(minuend) || !_.isNumber(subtrahend)) {
    return undefined;
  }

  const minuendNumber = minuend;
  const subtrahendNumber = subtrahend;
  const difference = minuendNumber - subtrahendNumber;

  const formatFn = borrowingBaseSummaryFormatterMap[valueType];
  const formattedMinuendValue = formatFn(minuendNumber);
  const formattedSubtrahendValue = formatFn(subtrahendNumber);
  // Use a string comparison on the formatted value for floating point comparison, in lieu of an
  // epsilon value.
  return difference === 0 || formattedMinuendValue === formattedSubtrahendValue
    ? undefined
    : difference;
};

// difference = Finley agent - borrower parsed
// if difference < 0, then borrower > finley
// if difference > 0, then borrower < finley
const isBorrowerMorePunitiveThanFinleyAgent = ({
  difference,
  fieldName,
}: {
  difference: number;
  fieldName: AllBorrowingBaseReportSummaryFields;
}): VerificationValueComparison => {
  const isBorrowerHigherValueMorePunitiveMapForField =
    isBorrowerHigherValueMorePunitiveMap[fieldName];

  if (isBorrowerHigherValueMorePunitiveMapForField !== undefined) {
    if (difference < 0) {
      return isBorrowerHigherValueMorePunitiveMapForField;
    }
    // if difference > 0, return the opposite
    return isBorrowerHigherValueMorePunitiveMapForField ===
      'borrowerHigherIsLessPunitive'
      ? 'borrowerHigherIsMorePunitive'
      : 'borrowerHigherIsLessPunitive';
  }

  return 'borrowerHigherIsLessPunitive';
};

export const getFormattedValueDifferenceTextAndFormatting = ({
  minuend,
  subtrahend,
  valueType,
  fieldName,
}: {
  minuend?: Util.Maybe<any>;
  subtrahend?: Util.Maybe<any>;
  valueType: BorrowingBaseSummaryValueType;
  fieldName: AllBorrowingBaseReportSummaryFields;
}): {
  valueDifferenceFormattedText: string;
  valueDifferenceBackgroundColor: string | undefined;
  valueDifferenceTextColor: string | undefined;
  valueDifferenceAlertIcon: boolean;
  valueDifferenceInfoIcon: boolean;
} => {
  const formatter = borrowingBaseSummaryFormatterMap[valueType];

  const valueDifference = getBorrowingBaseValueDifference({
    minuend,
    subtrahend,
    valueType,
  });

  const formatting: {
    valueDifferenceFormattedText: string;
    valueDifferenceBackgroundColor: string | undefined;
    valueDifferenceTextColor: string | undefined;
    valueDifferenceAlertIcon: boolean;
    valueDifferenceInfoIcon: boolean;
  } = {
    valueDifferenceFormattedText: formatter(valueDifference),
    valueDifferenceBackgroundColor: undefined,
    valueDifferenceTextColor: undefined,
    valueDifferenceAlertIcon: false,
    valueDifferenceInfoIcon: false,
  };

  // if at least one value doesn't exist, return default formatting
  if (!_.isNumber(minuend) || !_.isNumber(subtrahend)) {
    return formatting;
  }

  const differenceRateThreshold = 0.15;
  const valueDifferenceRate = commonUtils.divideByOrZero(
    Math.abs(valueDifference ?? 0),
    subtrahend ?? 0, // the value difference rate is based on the subtrahend (borrower) value
  );

  // only evaluate if there is a difference
  const isBorrowerValueMorePunitiveThanFinleyValue = valueDifference
    ? isBorrowerMorePunitiveThanFinleyAgent({
        difference: valueDifference,
        fieldName,
      })
    : undefined;

  // only show alert icon if Finley value is more punitive than Borrower value
  if (
    isBorrowerValueMorePunitiveThanFinleyValue ===
      'borrowerHigherIsLessPunitive' &&
    valueDifferenceRate > differenceRateThreshold
  ) {
    formatting.valueDifferenceAlertIcon = true;
  }

  if (
    isBorrowerValueMorePunitiveThanFinleyValue ===
    'borrowerHigherIsMorePunitive'
  ) {
    // if explicitly true (ie. there is a non-zero difference value and borrower is more punitive than finley)
    formatting.valueDifferenceBackgroundColor = colors.blue[100];
  } else if (
    isBorrowerValueMorePunitiveThanFinleyValue ===
    'borrowerHigherIsLessPunitive'
  ) {
    // if explicitly false (ie. there is a non-zero difference value and finley is more punitive than borrower)
    formatting.valueDifferenceBackgroundColor = colors.red[100];
    formatting.valueDifferenceTextColor = colors.error;
  }

  // if raw value difference is rounding error allowance or less, we display an info icon for rounding error
  if (
    valueDifference &&
    Math.abs(valueDifference) <= verificationRawValueRoundingErrorAllowance
  ) {
    formatting.valueDifferenceBackgroundColor = undefined;
    formatting.valueDifferenceTextColor = undefined;
    formatting.valueDifferenceAlertIcon = false;
    formatting.valueDifferenceFormattedText = '-';
    formatting.valueDifferenceInfoIcon = true;
  }

  return formatting;
};

export const groupMultiLpConcentrationKeysByLoanProducts = ({
  multiLoanProductConcentrationLimits,
}: {
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
}): {
  [key: string]: string[];
} => {
  // group multi-lp concentration limit keys by the lps involved
  const multiLpConcentrationKeysByLoanProductsGrouping: {
    [key: string]: string[];
  } = {};

  _.forEach(multiLoanProductConcentrationLimits, (multiLpConfig) => {
    // create a string with the loan product slugs
    const loanProductSlugsStr = _.chain(multiLpConfig.loanProducts)
      .map((lp) => commonUtils.toTitleCase(lp.entity.slug))
      .sort()
      .join(' & \n')
      .value();

    if (!multiLpConcentrationKeysByLoanProductsGrouping[loanProductSlugsStr]) {
      multiLpConcentrationKeysByLoanProductsGrouping[loanProductSlugsStr] = [];
    }

    if (multiLpConfig._config_?.concentrationLimitConfig?.propertyKey) {
      multiLpConcentrationKeysByLoanProductsGrouping[loanProductSlugsStr].push(
        multiLpConfig._config_.concentrationLimitConfig.propertyKey,
      );
    }
  });
  return multiLpConcentrationKeysByLoanProductsGrouping;
};
